/**
 * Contactregels op één plek: e-mail controleren en telefoonlinks maken.
 * Gebruikt door inloggen, afspraak boeken, Mijn gegevens en het beheer.
 */

import type { Country } from '../types/requests'

/** Genoeg om tikfouten te vangen ("naam@gmail"), zonder echte adressen af te wijzen. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export const isValidEmail = (email: string) => EMAIL.test(email.trim())

/**
 * Alleen links met een veilig schema. Instellingen zijn vrij te bewerken in het beheer;
 * een typfout of een `javascript:`-link mag nooit als klikbare link op de site komen.
 */
const SAFE_SCHEMES = ['https:', 'mailto:', 'tel:']

export function isSafeHref(href: string): boolean {
  try {
    return SAFE_SCHEMES.includes(new URL(href.trim()).protocol)
  } catch {
    return false
  }
}

/** Geeft `href` terug als die veilig is, anders `fallback`. */
export const safeHref = (href: string, fallback: string) => (isSafeHref(href) ? href.trim() : fallback)

/**
 * De ingesloten Google-kaart voor een adres, zonder API-sleutel.
 * Het adres komt uit de instellingen, dus de kaart verhuist mee als Sarah verhuist.
 * Let op: de site moet `frame-src https://www.google.com` toestaan in de CSP.
 */
export const mapEmbedUrl = (address: string) =>
  `https://www.google.com/maps?q=${encodeURIComponent(address.trim())}&z=16&output=embed`

/**
 * Postcodes voor de landen waar we naartoe verzenden. Eén bron voor afrekenen en Mijn gegevens;
 * de server controleert hetzelfde en rekent de verzendkosten per land.
 */
const POSTCODES: Record<Country, RegExp> = {
  NL: /^[1-9]\d{3}\s?[a-z]{2}$/i,
  BE: /^[1-9]\d{3}$/,
}

export const isPostcode = (value: string, country: Country) => POSTCODES[country].test(value.trim())

/** Voor een bewaard adres zonder land: vier cijfers zonder letters is België. */
export const countryForPostcode = (postcode: string): Country => (POSTCODES.BE.test(postcode.trim()) ? 'BE' : 'NL')

export const postcodeExample: Record<Country, string> = { NL: '1234 AB', BE: '1000' }

/** "1234ab" → "1234 AB", zoals PostNL het verwacht. Belgische postcodes blijven zoals ze zijn. */
export function formatPostcode(v: string): string {
  const m = v.trim().match(/^(\d{4})\s?([a-z]{2})$/i)
  return m?.[1] && m[2] ? `${m[1]} ${m[2].toUpperCase()}` : v.trim()
}

/** "Javastraat" + "41-2" → "Javastraat 41-2". Voor pakbon, factuur, profiel en klantdossier. */
export const formatStreet = (a: { street: string; houseNumber: string }) => [a.street.trim(), a.houseNumber.trim()].filter(Boolean).join(' ')

/** "06 12 34 56 78" → "tel:0612345678". */
export const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`
